type StorageItem<T> = {
  value: T;
  expirationTime: number | null;
  maxCount?: number;
};

type ExpiryTime = 'midnight' | number;

type SetOptions = {
  expiryTime?: ExpiryTime;
};

type SetListItemsOptions = {
  expiryTime?: ExpiryTime;
  maxCount?: number;
};

function calculateExpiryTime(expiryTime: ExpiryTime): number | null {
  if (expiryTime === 'midnight') {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return midnight.getTime();
  }
  return expiryTime ? Date.now() + expiryTime * 1000 : null;
}

/**
 * Sets the value in localStorage.
 * @param key The key to store the value against.
 * @param value The value to store.
 * @param expiryTime The expiry time in seconds like 5secs = 5.
 * @param maxCount The maximum number of items to store.
 */
function set<T>(key: string, value: T, options?: SetOptions): void {
  const item: StorageItem<T> = {
    value: value,
    expirationTime: options?.expiryTime ? calculateExpiryTime(options?.expiryTime) : null
  };
  localStorage.setItem(key, JSON.stringify(item));
}

/**
 * Gets the value from localStorage.
 * @param key The key to get the value for.
 * @returns The value stored against the key.
 */
function get<T>(key: string): T | null {
  const itemString = localStorage.getItem(key);
  if (!itemString) return null;

  const item = JSON.parse(itemString);

  if (item && (!item.expirationTime || item.expirationTime >= Date.now())) {
    return item.value;
  }

  remove(key);
  return null;
}

/**
 * Gets the value from localStorage.
 * @param key The key to get the value for.
 * @returns The value stored against the key.
 */
function getItemDetails<T>(key: string): StorageItem<T> | null {
  const itemString = localStorage.getItem(key);
  if (!itemString) return null;

  const item = JSON.parse(itemString) as StorageItem<T>;

  if (item && (!item.expirationTime || item.expirationTime >= Date.now())) {
    return item;
  }

  remove(key);
  return null;
}

/**
 * Sets List/Array value in localStorage.
 * @param key The key to store the value against.
 * @param value The value to store. Must be an array.
 * @param options The options to set the value. expiryTime and maxCount.
 */
function setListItems<T>(key: string, value: T, options?: SetListItemsOptions): void {
  if (options?.maxCount && !Array.isArray(value)) {
    throw Error('Value must be an array to use maxCount.');
  }
  const item: StorageItem<T> = {
    value: value,
    expirationTime: options?.expiryTime ? calculateExpiryTime(options?.expiryTime) : null,
    maxCount: options?.maxCount
  };
  localStorage.setItem(key, JSON.stringify(item));
}

/**
 *  Appends the value to the existing value in localStorage.
 * @param key The key to get the value for.
 * @param value The value to append.
 */
function appendListItem<T>(key: string, value: T): void {
  const itemDetails = getItemDetails<T>(key);

  if (!itemDetails) {
    throw Error(`No existing List found for key: ${key}`);
  }
  const { value: existingValue, maxCount } = itemDetails;
  if (existingValue) {
    if (!Array.isArray(existingValue)) {
      throw Error(`Existing value is not an array for key: ${key}`);
    }
    const totalCount = existingValue.length;
    const appendedValue = [...existingValue, value];
    if (maxCount && totalCount >= maxCount) {
      appendedValue.shift();
    }
    setListItems(key, appendedValue, { maxCount });
  }
}

/**
 * Updates the maxCount of the existing value in localStorage.
 * @param key The key to get the value for.
 * @param maxCount The maximum number of items to store.
 */
function updateMaxCount<T>(key: string, maxCount: number): void {
  const itemDetails = getItemDetails<T>(key);
  if (!itemDetails) {
    throw Error(`No existing List found for key: ${key}`);
  }
  const newItem = { ...itemDetails, maxCount };
  localStorage.setItem(key, JSON.stringify(newItem));
}

/**
 * Removes the value from the existing value in localStorage.
 * @param key The key to get the value for.
 */
function remove(key: string): void {
  localStorage.removeItem(key);
}

/**
 * Clears the localStorage.
 */
function reset(): void {
  localStorage.clear();
}

/**
 * A wrapper around localStorage to store user preferences.
 * Always use this inside a try-catch block.
 */
export const PersistentStore = {
  set,
  get,
  setListItems,
  appendListItem,
  updateMaxCount,
  remove,
  reset
};
